/**
 * A covering provider's portal access, and the manager's login link (ruling,
 * Isaiah Cotto, 2026-10-19).
 *
 * Coverage grants portal access at the building -- but only to a login, and
 * coverage keys on the provider's workforce record, not a login. So a manager
 * links the two: they search for the login by what they type, pick it, say
 * why, and confirm. Nothing is matched or suggested by name or email; the
 * search answers only what was typed. Linking creates the provider's missing
 * grants for every building they cover, in the same server transaction;
 * unlinking ends them. Both are recorded (who, which login, why) and shown in
 * the history here.
 *
 * The server decides who may do this (operations.scheduling.manage at
 * enterprise scope) and refuses everyone else in words this panel shows.
 *
 *   GET  /emerge/provider-assignments/providers/:employeeId/access
 *   GET  /emerge/provider-assignments/logins?q=
 *   POST /emerge/provider-assignments/providers/:employeeId/login-link    { userId, reason }
 *   POST /emerge/provider-assignments/providers/:employeeId/login-unlink  { reason }
 */
import React, { useCallback, useEffect, useState } from 'react';
import api from '../../services/api';
import { outcomeOf, shortDate } from '../finance/financeApi';
import type { ApiResult } from '../finance/financeApi';

export type PortalAccess = 'IN_EFFECT' | 'NOT_IN_EFFECT' | 'WAITING_ON_LOGIN_LINK' | null;

/** What a covering row's portal access is, in a manager's words. */
export const PORTAL_ACCESS_WORDS: Record<Exclude<PortalAccess, null>, string> = {
  IN_EFFECT: 'portal access in effect',
  NOT_IN_EFFECT: 'login linked, no portal access here yet',
  WAITING_ON_LOGIN_LINK: 'portal access waiting on the login link',
};

interface AccessStatus {
  /** A facility-scoped manager's view (verification 2026-10-19): their buildings only, no login details, no history. */
  scoped?: boolean;
  employee: { id: string; name: string | null; is_active: boolean };
  login: { linked?: boolean; user_id: string | null; email: string | null; name: string | null; is_active: boolean | null; activation_state: string | null } | null;
  waiting_on_login_link: boolean;
  coverage: Array<{ id: string; facility_id: string; facility_name: string; coverage_type: string; access_in_effect: boolean; building_closed?: boolean; closure_words?: string | null }>;
  history: Array<{ id: string; event: 'LINKED' | 'UNLINKED'; user_id: string; reason: string; created_at: string; login_email: string | null; actor_name: string | null }>;
}
interface LoginOption { id: string; email: string; name: string | null; is_active: boolean | null; activation_state: string | null; linked_employee_id: string | null }
interface LinkOutcome { changed: boolean; grants?: Array<{ outcome: string }>; revoked?: Array<{ outcome: string }> }

const BASE = '/emerge/provider-assignments';
export const providerAccessApi = {
  status: async (employeeId: string) => (await api.get(`${BASE}/providers/${employeeId}/access`)) as ApiResult<AccessStatus>,
  searchLogins: async (q: string) => (await api.get(`${BASE}/logins?q=${encodeURIComponent(q)}`)) as ApiResult<{ items: LoginOption[]; query: string; note?: string }>,
  link: async (employeeId: string, userId: string, reason: string) => (await api.post(`${BASE}/providers/${employeeId}/login-link`, { userId, reason })) as ApiResult<LinkOutcome>,
  unlink: async (employeeId: string, reason: string) => (await api.post(`${BASE}/providers/${employeeId}/login-unlink`, { reason })) as ApiResult<LinkOutcome>,
};

const count = (rows: Array<{ outcome: string }> | undefined, outcome: string) => (rows || []).filter((r) => r.outcome === outcome).length;

export default function ProviderAccess({ employeeId, providerName, onChanged }: {
  employeeId: string; providerName: string; onChanged: (message: string) => Promise<void> | void;
}) {
  const [status, setStatus] = useState<AccessStatus | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [results, setResults] = useState<LoginOption[] | null>(null);
  const [searchNote, setSearchNote] = useState<string | null>(null);
  const [picked, setPicked] = useState('');
  const [reason, setReason] = useState('');
  const [unlinking, setUnlinking] = useState(false);
  const [rechecking, setRechecking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoadErr(null);
    try {
      const r = await providerAccessApi.status(employeeId);
      if (r.success && r.data) setStatus(r.data); else setLoadErr(outcomeOf(r));
    } catch { setLoadErr('Could not reach the server.'); }
  }, [employeeId]);
  useEffect(() => { load(); }, [load]);

  const search = async () => {
    setErr(null); setPicked(''); setSearchNote(null);
    try {
      const r = await providerAccessApi.searchLogins(q.trim());
      if (!r.success || !r.data) { setResults(null); setErr(outcomeOf(r)); return; }
      setResults(r.data.items || []);
      setSearchNote(r.data.note || null);
    } catch { setErr('Could not reach the server.'); }
  };

  const act = async (fn: () => Promise<ApiResult<LinkOutcome>>, done: (d: LinkOutcome) => string) => {
    setBusy(true); setErr(null);
    try {
      const r = await fn();
      if (!r.success || !r.data) { setErr(outcomeOf(r, { wrote: true })); return; }
      setReason(''); setPicked(''); setResults(null); setQ(''); setUnlinking(false); setRechecking(false);
      await load();
      await onChanged(done(r.data));
    } catch { setErr('Could not reach the server. Nothing was recorded.'); } finally { setBusy(false); }
  };

  if (loadErr) return <p className="um-notice stop" role="alert">{loadErr}</p>;
  if (!status) return <p className="um-quiet">Loading portal access…</p>;

  const why = reason.trim();
  return (
    <section className="sc-access" aria-label={`Portal access for ${providerName}`}>
      {status.login && status.scoped ? (
        <p>A login is linked. Its details and the link history are shown to enterprise managers only.</p>
      ) : status.login ? (
        <p>
          Login: <strong>{status.login.email || 'no email on the login'}</strong>
          {status.login.name ? ` (${status.login.name})` : ''}
          {status.login.activation_state && status.login.activation_state !== 'ACTIVE'
            ? <span className="sc-src">not signed in yet: {status.login.activation_state.toLowerCase().replace(/_/g, ' ')}</span> : null}
        </p>
      ) : (
        <p className="um-notice" role="note">
          No login is linked to {providerName}. Coverage is recorded; portal access is waiting on the login link.
        </p>
      )}
      {status.login && !status.scoped && status.login.is_active === false && status.login.activation_state !== 'ACTIVE' && (
        <p className="um-notice" role="note">
          This login has not been activated yet, so it holds no portal access. After {providerName} activates it, choose
          Re-check portal access: nothing grants it automatically on sign-in.
        </p>
      )}
      {status.coverage.length > 0 && (
        <ul aria-label="Buildings covered">
          {status.coverage.map((c) => (
            <li key={c.id}>
              {c.facility_name} · {c.coverage_type.toLowerCase()} · {c.access_in_effect ? 'portal access in effect' : 'no portal access'}
              {c.building_closed ? <span className="sc-src">{c.closure_words || 'This building is closed for providers'}: no new grant here; ending this coverage removes its access</span> : null}
            </li>
          ))}
        </ul>
      )}

      {!status.login && !status.scoped && (
        <form className="um-form" aria-label={`Link a login to ${providerName}`} onSubmit={(e) => { e.preventDefault(); if (picked && why.length >= 3) act(() => providerAccessApi.link(employeeId, picked, why), (d) => `${providerName}: login linked; portal access granted at ${count(d.grants, 'GRANTED')} building(s).`); }}>
          <label>
            <span>Find the login (type part of its email or name)</span>
            <input value={q} onChange={(e) => setQ(e.target.value)} maxLength={120} aria-label="Find a login" />
          </label>
          <div className="btns">
            <button type="button" className="um-open" disabled={busy || q.trim().length < 2} onClick={search}>Search logins</button>
          </div>
          {searchNote && <p className="um-quiet">{searchNote}</p>}
          {results && results.length === 0 && <p className="um-quiet">No workforce login matches &ldquo;{q.trim()}&rdquo;.</p>}
          {results && results.length > 0 && (
            <fieldset>
              <legend>Pick the login that is {providerName}&rsquo;s</legend>
              {results.map((u) => {
                const elsewhere = Boolean(u.linked_employee_id && u.linked_employee_id !== employeeId);
                return (
                  <label key={u.id} className="sc-access-option">
                    <input type="radio" name={`login-${employeeId}`} value={u.id} checked={picked === u.id} disabled={elsewhere}
                      onChange={() => setPicked(u.id)} aria-label={`${u.email}${elsewhere ? ' (linked to another provider)' : ''}`} />
                    <span>{u.email}{u.name ? ` · ${u.name}` : ''}{elsewhere ? ' · linked to another provider' : ''}</span>
                  </label>
                );
              })}
            </fieldset>
          )}
          <label><span>Why this is their login (required)</span><input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} aria-label="Why this is their login" /></label>
          <div className="btns">
            <button type="submit" className="um-open um-primary" disabled={busy || !picked || why.length < 3}>{busy ? 'Recording…' : 'Confirm: Link login'}</button>
          </div>
        </form>
      )}

      {status.login && !status.scoped && !unlinking && !rechecking && (
        <div className="sc-cmds">
          <button type="button" className="um-open" onClick={() => { setRechecking(true); setErr(null); }}>Re-check portal access</button>
          <button type="button" className="um-open" onClick={() => { setUnlinking(true); setErr(null); }}>Unlink login</button>
        </div>
      )}
      {status.login && !status.scoped && rechecking && status.login.user_id && (
        <form className="um-form" aria-label={`Re-check portal access for ${providerName}`} onSubmit={(e) => { e.preventDefault(); const uid = status.login && status.login.user_id; if (uid && why.length >= 3) act(() => providerAccessApi.link(employeeId, uid, why), (d) => `${providerName}: portal access re-checked; granted at ${count(d.grants, 'GRANTED')} building(s).`); }}>
          <p className="um-quiet">Re-checking grants the portal access this provider&rsquo;s coverage could not have before (for example, before their login was activated). It changes nothing else.</p>
          <label><span>Why (required)</span><input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} aria-label="Why re-check" /></label>
          <div className="btns">
            <button type="submit" className="um-open um-primary" disabled={busy || why.length < 3}>{busy ? 'Recording…' : 'Confirm: Re-check portal access'}</button>
            <button type="button" className="um-open" onClick={() => setRechecking(false)} disabled={busy}>Back</button>
          </div>
        </form>
      )}
      {status.login && unlinking && (
        <form className="um-form" aria-label={`Unlink the login from ${providerName}`} onSubmit={(e) => { e.preventDefault(); if (why.length >= 3) act(() => providerAccessApi.unlink(employeeId, why), (d) => `${providerName}: login unlinked; portal access removed at ${count(d.revoked, 'REVOKED')} building(s).`); }}>
          <p className="um-quiet">Unlinking removes the portal access this provider&rsquo;s coverage granted. Coverage itself stays.</p>
          <label><span>Why (required)</span><input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} aria-label="Why unlink" /></label>
          <div className="btns">
            <button type="submit" className="um-open um-primary" disabled={busy || why.length < 3}>{busy ? 'Recording…' : 'Confirm: Unlink login'}</button>
            <button type="button" className="um-open" onClick={() => setUnlinking(false)} disabled={busy}>Back</button>
          </div>
        </form>
      )}

      {err && <p className="um-notice stop" role="alert">{err}</p>}

      {status.history.length > 0 && (
        <ul aria-label="Login link history" className="sc-access-history">
          {status.history.slice(0, 5).map((h) => (
            <li key={h.id}>
              {shortDate(h.created_at)} · {h.event === 'LINKED' ? 'linked' : 'unlinked'} {h.login_email || 'a login'}
              {h.actor_name ? ` by ${h.actor_name}` : ''} · {h.reason}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
